/* eslint-disable jsx-a11y/alt-text */
import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import SearchInput from "src/components/widgets/SearchInput";
import NewAccountModal from "src/components/modals/NewAccountModal";
import CustomModal from "src/components/CustomModal";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { disableAccount, getAccounts } from "src/api/accounts";
import CustomDropdown from "src/components/CustomDropdown";
import { Button } from "src/components/ui/button";
import { EllipsisIcon } from "src/svg";
import CustomConfirmDialog from "src/components/CustomConfirmDialog";
import { showSuccessToast } from "src/lib/utils";
import PermissionWrapper from "src/components/PermissionWrapper";
import useHasPermissions from "src/hooks/useHasPermissions";
import "src/array";

const Accounts = () => {
	const { data } = useQuery({
		queryKey: ["accounts"],
		queryFn: getAccounts,
		suspense: true,
	});

	const accounts = data?.data ?? [];

	return (
		<div className="pageWrapper">
			<div className="pageTop">
				<h2>Bank Accounts</h2>
			</div>

			<div className="innerContent">
				<ActionBar />

				{accounts.length > 0 ? (
					<div className="tw-mt-10 tw-grid tw-gap-6 tw-grid-cols-[repeat(auto-fill,minmax(260px,1fr))]">
						{accounts.map((account, index) => (
							<Account account={account} key={index} />
						))}
					</div>
				) : (
					<div className="!tw-p-8 mb-4 dash-tables tw-text-center">
						<p>You have not added a bank account.</p>
					</div>
				)}
			</div>
		</div>
	);
};

const ActionBar = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const search = searchParams.get("search");

	const updateSearchParams = (key, value) => {
		const newSearchParams = new URLSearchParams(searchParams);

		newSearchParams.set(key, value);

		setSearchParams(newSearchParams);
	};

	return (
		<div className="tw-gap-4 inner-top tw-flex-wrap">
			<SearchInput
				name="search_text"
				type="text"
				placeholder="Search..."
				value={search}
				onChange={(event) => updateSearchParams("search", event.target.value)}
			/>
			<div className="inner-top-right tw-ml-auto tw-items-center tw-gap-4 tw-flex-wrap">
				<PermissionWrapper permission="add_bank_account">
					<CustomModal title="Add Bank Account" trigger={<button className="blue-btn">Add New Bank Account</button>}>
						<NewAccountModal />
					</CustomModal>
				</PermissionWrapper>

				<Link to="/disabled-accounts" className="btn green-btn">
					View Disabled Accounts
				</Link>
			</div>
		</div>
	);
};

const Account = ({ account }) => {
	const { bank_name, account_number, tag_name } = account;
	const queryClient = useQueryClient();
	const hasDisablePermission = useHasPermissions("disable_bank_account");
	const hasUpdatePermission = useHasPermissions("update_bank_account");

	const { mutate: disable, isLoading: isDisableLoading } = useMutation(disableAccount, {
		onSuccess: ({ message }) => {
			showSuccessToast(message);
			queryClient.invalidateQueries(["accounts"]);
		},
	});

	return (
		<div className="bank-acc-wrap">
			<div className="tw-flex tw-items-start tw-justify-between">
				<div className="acc-itm">
					<p className="small-txt">Bank Name</p>
					<h3>{bank_name}</h3>
				</div>
				<CustomDropdown
					trigger={
						<Button variant="hover">
							<EllipsisIcon />
						</Button>
					}
					actions={[]
						.appendToArray(
							[
								{
									label: (
										<CustomModal title="Update Bank Account" trigger={<span>Update Bank Account</span>}>
											<NewAccountModal account={account} />
										</CustomModal>
									),
								},
							],
							hasUpdatePermission
						)
						.appendToArray(
							[
								{
									label: (
										<CustomConfirmDialog
											trigger={<span>Disable Account</span>}
											mutateFn={() => disable({ uuid: account.uuid })}
										/>
									),
									disabled: isDisableLoading,
									variant: "destructive",
								},
							],
							hasDisablePermission
						)}
				/>
			</div>
			<div className="acc-itm">
				<p className="small-txt">Account Number</p>
				<h3>{account_number}</h3>
			</div>
			<div className="">
				<p>Tag: {tag_name}</p>
			</div>
		</div>
	);
};

export default Accounts;
